// Pieces of the parent registration screen (RegisterPage.jsx). Plain
// presentational components; the rules live in registrationModel.js.
import React, { useEffect, useRef, useState } from "react";
import { pickedOnPage, money, titleCase } from "./registrationModel";

export const Icon = ({ name, style }) => (
  <span className="msr" aria-hidden="true" style={style}>{name}</span>
);


/** One tab per child (2+ children), with their pick count, and "Add dancer". */
export const ChildTabs = ({ dancers, activeId, picks, eligible, onPick, onAdd, canAdd }) => (
  <div className="rg-kids" role="tablist" aria-label="Dancers">
    {dancers.map((d) => {
      const id = String(d._id);
      const n = (picks[id] || []).length;
      const entered = d.isRegistered;
      const cls = ["rg-kid", id === activeId && "on", n > 0 && "has", entered && "entered", !entered && !eligible[id] && "off"]
        .filter(Boolean)
        .join(" ");
      return (
        <button key={id} type="button" role="tab" aria-selected={id === activeId} className={cls} onClick={() => onPick(id)}>
          {d.fname}
          {entered ? (
            <span className="cnt" aria-label="entered"><Icon name="check_circle" /></span>
          ) : (
            <span className="cnt">{n}</span>
          )}
        </button>
      );
    })}
    {canAdd && (
      <button type="button" className="rg-kid add" onClick={onAdd}>
        <Icon name="add" />Add dancer
      </button>
    )}
  </div>
);

/**
 * The level carousel: every page in one scrolling bar, the current one kept
 * in the middle. Counts show where picks are.
 */
export const LevelBar = ({ pages, activeKey, picked, onPick }) => {
  const barRef = useRef(null);
  // More levels off to a side: a fade and a green arrow on that side
  // (Devin 2026-10-17); the arrow moves to the next / previous level.
  const [more, setMore] = useState({ left: false, right: false });
  const measure = () => {
    const bar = barRef.current;
    if (!bar) return;
    const left = bar.scrollLeft > 2;
    const right = bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 2;
    setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
  };
  useEffect(() => {
    const bar = barRef.current;
    const el = bar ? [...bar.children].find((c) => c.dataset.key === activeKey) : null;
    if (!bar || !el) return;
    const left = el.offsetLeft - (bar.clientWidth - el.clientWidth) / 2;
    if (typeof bar.scrollTo === "function") bar.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    measure();
  }, [activeKey, pages.length]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const step = (dir) => {
    const i = pages.findIndex((p) => p.key === activeKey);
    const next = pages[i + dir];
    if (next) onPick(next.key);
    else barRef.current?.scrollBy?.({ left: dir * barRef.current.clientWidth * 0.6, behavior: "smooth" });
  };
  return (
    <div className={`rg-levels${more.left ? " more-left" : ""}${more.right ? " more-right" : ""}`}>
      {more.left && (
        <button type="button" className="rg-lv-arrow left" aria-label="Previous level" onClick={() => step(-1)}>
          <Icon name="chevron_left" />
        </button>
      )}
      {more.right && (
        <button type="button" className="rg-lv-arrow right" aria-label="Next level" onClick={() => step(1)}>
          <Icon name="chevron_right" />
        </button>
      )}
      <div className="rg-levels-in" ref={barRef} role="tablist" aria-label="Levels" onScroll={measure}>
        {pages.map((p) => {
          const n = pickedOnPage(p, picked);
          return (
            <button
              key={p.key}
              data-key={p.key}
              type="button"
              role="tab"
              aria-selected={p.key === activeKey}
              className={`rg-lt${p.key === activeKey ? " on" : ""}`}
              onClick={() => onPick(p.key)}
            >
              {p.title}
              {n > 0 && <span className="c">{n}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const roundsText = (c) => (Number(c?.rounds) > 1 ? `${c.rounds} rounds` : "");

const Row = ({ comp, on, disabled, sub, muted, note, onToggle }) => {
  const name = titleCase(comp.description || comp.code);
  const extra = [comp.code, roundsText(comp)].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      className={`rg-row${on ? " on" : ""}${sub ? " sub" : ""}${muted ? " muted" : ""}`}
      aria-pressed={on}
      aria-label={sub ? `2nd chance for ${name.replace(/[\s-]*second( chance)?/i, "")}` : undefined}
      disabled={disabled}
      onClick={() => onToggle(String(comp._id))}
    >
      <span className="rg-tick">{on && <Icon name="check" />}</span>
      <span className="nm">
        {/* under its dance, a second chance needs no repeat of the name */}
        {sub ? "2nd chance" : name}
        <small>{note || extra}</small>
      </span>
      <span className="pr">{Number(comp.price) > 0 ? money(Math.round(Number(comp.price) * 100)) : "Free"}</span>
    </button>
  );
};

/** One carousel page: its dances (second chances under their dance) and its specials. */
export const PageList = ({ page, picked, onToggle, teamNote, elsewhere, kept, keptNote }) => {
  const set = new Set((picked || []).map(String));
  const rows = (list) => (
    <div className="rg-card">
      {list.map(({ comp, seconds }) => (
        <React.Fragment key={comp._id}>
          <Row
            comp={comp}
            on={set.has(String(comp._id))}
            muted={elsewhere?.has(String(comp._id))}
            note={
              kept?.has(String(comp._id))
                ? `${comp.code} · ${keptNote}`
                : elsewhere?.has(String(comp._id))
                  ? `Picked at ${elsewhere.get(String(comp._id))}`
                  : undefined
            }
            onToggle={onToggle}
          />
          {seconds.map((s) => {
            const mainOn = set.has(String(comp._id));
            return (
              <Row
                key={s._id}
                comp={s}
                sub
                on={set.has(String(s._id))}
                // needs its main to be ticked, but a ticked one can always be unticked
                disabled={!mainOn && !set.has(String(s._id))}
                note={mainOn ? s.code : `Tick ${titleCase(comp.description || comp.code)} first`}
                onToggle={onToggle}
              />
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
  return (
    // dances and the level's specials side by side on a wide screen; a level
    // with no specials uses the full width
    <div className={`rg-grid${page.specials.length > 0 ? "" : " solo"}`}>
      <div>
        {teamNote}
        {rows(page.rows)}
      </div>
      {page.specials.length > 0 && (
        <div>
          <div className="rg-sub">Specials for {page.title}</div>
          {rows(page.specials)}
        </div>
      )}
    </div>
  );
};

/**
 * Summary rows by section (registrationModel summarySections): a small
 * heading per level / Specials / Teams, one row per dance, a second chance
 * on its own indented line under its dance.
 */
export const SummarySections = ({ sections, prices = true }) =>
  sections.map((s) => (
    <div className="rg-sum-s" key={s.key}>
      <div className="rg-sum-st">{s.title}</div>
      {s.rows.map((r) => (
        <div className={`rg-sum-l${r.sub ? " sub" : ""}`} key={r.id}>
          <span className="n">
            {r.name}
            {r.secondOnly && <small>2nd chance</small>}
          </span>
          {prices && <span className="p">{money(r.cents)}</span>}
        </div>
      ))}
    </div>
  ));

/** A dancer's picks for the summary panel / review: header plus sections. */
export const DancerSummary = ({ dancer, sections, count, onEdit, editLabel = "Edit" }) => {
  const cents = sections.reduce((s, x) => s + x.rows.reduce((t, r) => t + r.cents, 0), 0);
  const n = count ?? sections.reduce((s, x) => s + x.rows.length, 0);
  return (
    <div className="rg-sum-d">
      <div className="rg-sum-h">
        <span className="nm">
          {dancer.fname}
          <small>{n === 1 ? "1 entry" : `${n} entries`}</small>
        </span>
        <span className="amt">{money(cents)}</span>
        {onEdit && (
          <button type="button" className="rg-edit" onClick={onEdit}><Icon name="edit" />{editLabel}</button>
        )}
      </div>
      <SummarySections sections={sections} />
    </div>
  );
};

const FEE_LABELS = {
  items: "Entries",
  family_max: "Family maximum",
  family_fee: "Family fee",
  late_fee: "Late fee",
  service_fee: "Platform Fee",
  difference: "Added dances",
};

/** The server quote as fee lines and a total (one "Platform Fee" line, Devin 2026-10-17). */
export const FeeLines = ({ quote, loading, failed, error, onRetry }) => {
  if (!quote && failed) {
    return (
      <div className="rg-note error" style={{ margin: 12 }}>
        <Icon name="error" />
        <div>
          {error || "We couldn't work out the price."}
          {onRetry && (
            <div><button type="button" className="rg-link" onClick={onRetry}><Icon name="refresh" />Try again</button></div>
          )}
        </div>
      </div>
    );
  }
  if (!quote) {
    return (
      <div className="rg-fee tot">
        <span className="k">Total</span>
        <span className="v" style={{ fontSize: 15, fontWeight: 500, color: "var(--text-subtle)" }}>
          {loading ? "Working it out…" : "—"}
        </span>
      </div>
    );
  }
  const currency = String(quote.currency || "").toUpperCase();
  return (
    <>
      {(quote.lines || []).map((l) => (
        <div className="rg-fee" key={l.type}>
          <span className="k">
            {l.type === "difference" && l.amountCents < 0 ? "Removed (no refund)" : FEE_LABELS[l.type] || l.label}
          </span>
          <span className="v">{l.amountCents < 0 ? `−${money(-l.amountCents)}` : money(l.amountCents)}</span>
        </div>
      ))}
      <div className="rg-fee tot">
        <span className="k">Total</span>
        <span className="v">
          {currency && <small>{currency}</small>}
          {`$${(Number(quote.totalCents || 0) / 100).toFixed(2)}`}
        </span>
      </div>
    </>
  );
};

const YEARS = (() => {
  const now = new Date().getFullYear();
  const out = [];
  for (let y = now - 2; y >= now - 90; y -= 1) out.push(y);
  return out;
})();

/**
 * Add a dancer without leaving registration: name (surname and school copied
 * from a brother or sister), birth year, boy/girl, school. Calls `onSave`
 * with the body POST /account/dancers expects.
 */
export const AddDancerSheet = ({ schools, sibling, saving, error, onSave, onClose, children }) => {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState(sibling?.lname || "");
  const [year, setYear] = useState("");
  const [gender, setGender] = useState("");
  const [schoolId, setSchoolId] = useState(sibling?.school_id ? String(sibling.school_id) : "");
  const [tried, setTried] = useState(false);
  const firstRef = useRef(null);
  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const school = (schools || []).find((s) => String(s._id) === schoolId);
  const missing = {
    first: !first.trim(),
    last: !last.trim(),
    year: !year,
    gender: !gender,
    school: !school,
  };
  const ok = !Object.values(missing).some(Boolean);
  const cap = (s) => s.trim().charAt(0).toUpperCase() + s.trim().slice(1);
  const save = () => {
    setTried(true);
    if (!ok || saving) return;
    onSave({
      first_name: cap(first),
      last_name: cap(last),
      date_birth: String(year),
      gender,
      school: school.schoolName,
      school_id: school._id,
    });
  };
  const err = (k, msg) => tried && missing[k] && <div className="rg-err">{msg}</div>;
  return (
    <>
      <div className="rg-scrim" onClick={onClose} />
      <div className="rg-sheet" role="dialog" aria-modal="true" aria-label="Add a dancer">
        <div className="grab" />
        {children || (
          <>
            <h3>Add a dancer</h3>
            <p className="s">They'll be saved to your account for next time.</p>
            <div className="rg-two">
              <div className="rg-f">
                <label htmlFor="rg-first">First name</label>
                <input id="rg-first" ref={firstRef} className="rg-in" maxLength={30} value={first} onChange={(e) => setFirst(e.target.value)} />
                {err("first", "Enter a first name")}
              </div>
              <div className="rg-f">
                <label htmlFor="rg-last">Last name</label>
                <input id="rg-last" className="rg-in" maxLength={30} value={last} onChange={(e) => setLast(e.target.value)} />
                {err("last", "Enter a last name")}
              </div>
            </div>
            <div className="rg-two">
              <div className="rg-f">
                <label htmlFor="rg-year">Birth year</label>
                <select id="rg-year" className="rg-in" value={year} onChange={(e) => setYear(e.target.value)}>
                  <option value="">Choose</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {err("year", "Choose the birth year")}
              </div>
              <div className="rg-f">
                <label>&nbsp;</label>
                <div className="rg-seg" role="group" aria-label="Girl or boy">
                  <button type="button" className={gender === "Girls/Ladies" ? "on" : ""} aria-pressed={gender === "Girls/Ladies"} onClick={() => setGender("Girls/Ladies")}>Girl</button>
                  <button type="button" className={gender === "Boys/Men" ? "on" : ""} aria-pressed={gender === "Boys/Men"} onClick={() => setGender("Boys/Men")}>Boy</button>
                </div>
                {err("gender", "Choose girl or boy")}
              </div>
            </div>
            <div className="rg-f">
              <label htmlFor="rg-school">Dance school</label>
              <select id="rg-school" className="rg-in" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
                <option value="">Choose the school</option>
                {(schools || []).map((s) => (
                  <option key={s._id} value={String(s._id)}>{s.schoolName}</option>
                ))}
              </select>
              {err("school", "Choose the dance school")}
            </div>
            {error && (
              <div className="rg-note error"><Icon name="error" /><div>{error}</div></div>
            )}
            <button type="button" className="rg-cta" style={{ marginTop: 6 }} disabled={saving} onClick={save}>
              {saving ? "Saving…" : `Add ${first.trim() ? cap(first) : "dancer"}`}
            </button>
          </>
        )}
      </div>
    </>
  );
};
